import { Structures } from "../api/compute/Structures";
import { TDate } from "./date/TDate";

export type ServerType =
  | "Commodity"
  | "Currency"
  | "ETF"
  | "Index"
  | "Sector"
  | "Stock";

export type OutputType = "HTML" | "PDF" | "TEXT";

const DAYS = {
  1: "Mon",
  2: "Thu",
  3: "Wed",
  4: "Tue",
  5: "Fri",
  6: "Sat",
  7: "Sun",
} as const;

const MONTHS = {
  "01": "Jan",
  "02": "Feb",
  "03": "Mar",
  "04": "Apr",
  "05": "May",
  "06": "Jun",
  "07": "Jul",
  "08": "Aug",
  "09": "Sep",
  "10": "Oct",
  "11": "Nov",
  "12": "Dec",
} as const;

export class Formatter {
  format: any;
  properties: any;
  strategyFormatter: any;
  taxonomy: any;
  taxonomies: any;
  taxonomyKeys: any;

  // short peer names for pinned objects
  PEER_SHORT_NAMES = {
    "Developed Markets - Americas": "Developed Markets - Americas",
    "Developed Markets - Asia Pacific": "Developed Markets - Asia",
    "Developed Markets - Europe & Middle East": "Developed Markets - Europe",

    "Emerging Markets - Asia": "Emerging Markets - Asia",
    "Emerging Markets - Americas": "Emerging Markets - Americas",
    "Emerging Markets - Europe, Middle East & Africa":
      "Emerging Markets - Europe and Africa",

    "Frontier Markets - Africa": "Frontier Markets - Africa",
    "Frontier Markets - Americas": "Frontier Markets - Americas",
    "Frontier Markets - Asia": "Frontier Markets - Asia",
    "Frontier Markets - Europe & CIS": "Frontier Markets - Europe",
    "Frontier Markets - Middle East": "Frontier Markets - Middle East",
  } as const;

  SERVER_TYPE_DEFAULT = "instrument" as const;
  SERVER_TYPES = [
    "Commodity",
    "Currency",
    "ETF",
    "Index",
    "Sector",
    "Stock",
  ] as ServerType[];

  async initFormatter() {
    const structuresAPI = new Structures();
    const properties = await structuresAPI.properties();
    this.setProperties(properties);
    const taxonomy = await structuresAPI.taxonomies();
    this.setTaxonomies(taxonomy);
  }

  setProperties(properties: ServerType[]) {
    const serverTypeDefault = this.SERVER_TYPE_DEFAULT;
    const serverTypes = this.SERVER_TYPES;

    const _properties: any = {};
    // specific properties for instruments (ETF, Index, Stock)
    for (let i = 0, length = serverTypes.length; i < length; i++) {
      const serverType: any = serverTypes[i];
      if (serverType in properties) {
        _properties[serverType.toLowerCase()] = properties[serverType];
      }
    }
    // default properties for instruments
    _properties[serverTypeDefault] = {};
    for (const property in properties) {
      if (!serverTypes.includes(property as ServerType)) {
        _properties[serverTypeDefault][property] = properties[property];
      }
    }

    this.properties = { ..._properties };

    // setting up input for notAvailable
    for (const clientType in this.properties) {
      for (const property in this.properties[clientType]) {
        this.properties[clientType][property] =
          this._preparePropertyConfiguration(
            property,
            this.properties[clientType][property]
          );
      }
    }
  }
  setTaxonomies(taxonomies: any) {
    this.taxonomies = taxonomies;
  }

  _preparePropertyConfiguration(
    property: any,
    sourcePropertyConfiguration: any
  ) {
    const propertyConfiguration = { ...sourcePropertyConfiguration };

    if (!propertyConfiguration.hasOwnProperty("notAvailable")) {
      console.warn(
        "[Misconfigured Property] notAvailable undefined ",
        property,
        propertyConfiguration
      );

      return propertyConfiguration;
    }

    const formatters = propertyConfiguration["formatter"];
    if (formatters == null) {
      return propertyConfiguration;
    }

    const notAvailable = propertyConfiguration["notAvailable"];

    for (const formatterName in formatters) {
      const formatter = formatters[formatterName];
      const options = formatter["options"];

      formatter["backendProperty"] = propertyConfiguration["backendProperty"];

      const outputNotAvailable = options["notAvailable"]["output"];
      formatter["options"]["notAvailable"] = {
        input: notAvailable,
        output: outputNotAvailable,
      };

      formatter["referenceProperties"] =
        formatter["referenceProperties"] == null
          ? null
          : formatter["referenceProperties"];
    }

    return propertyConfiguration;
  }

  /**
   * Format string
   *
   * @param {object}  params - value and formatting options
   * @param {object}  params.options - null or undefined
   * @param {boolean} params.options.hasToEscapeXmlEntities - if true, & are
   *      replaced with &amp;. Dafault false
   * @param {object}  params.options.notAvailable - (Required) Directive to manage not available case
   * @param {any}     params.options.notAvailable.input - not available value
   * @param {any}     params.options.notAvailable.output - what to return if params.value === to params.options.notAvailable.input
   * @param {string}  params.output - supported outputs are: "HTML" | "PDF" | "TEXT"
   * @param {number}  params.value - the value to be formatted
   * @param {object}  params.valueHelper - null or undefined
   *
   * @returns {string} a formatted string
   */
  string(params: any) {
    const hasToEscapeXmlEntities = this._getOption(
      params,
      "hasToEscapeXmlEntities",
      false
    );
    // notAvailable is an object with input and output properties
    // if input to be formatted is equal to notAvailable.input
    // then notAvailable.output is returned
    const notAvailable = this._getOption(params, "notAvailable", null);

    const output = params.output;
    let value = params.value;

    // shallow comparison wanted (like null for undefined / null)
    // eslint-disable-next-line eqeqeq
    if (value == notAvailable["input"]) {
      return notAvailable["output"];
    }

    // Set value as string, if if wasn't the case before
    // Do not do this before checking notAvailable
    value = String(value);

    function escapeXmlEntities(string: string) {
      return string.replace(/&/gi, "&amp;");
    }

    let formatted = "";
    switch (output) {
      case "HTML": {
        formatted = [
          '<span title="',
          hasToEscapeXmlEntities === true ? escapeXmlEntities(value) : value,
          '">',
          value,
          "</span>",
        ].join("");

        break;
      }
      case "PDF":
      case "TEXT":
      default: {
        formatted =
          hasToEscapeXmlEntities === true ? escapeXmlEntities(value) : value;

        break;
      }
    }

    return formatted;
  }

  /**
   * Format taxon
   *
   * @param {object} params - value and formatting options
   * @param {object} params.options - formatting options
   * @param {string} params.options.ancestorAtLevel - print ancestor
   *       at the ancestorAtLevel instead of the node
   * @param {object} params.options.notAvailable - (Required) Directive to manage not available case
   * @param {any}    params.options.notAvailable.input - not available value
   * @param {any}    params.options.notAvailable.output - what to return if params.value === to params.options.notAvailable.input
   * @param {object} params.options.taxonomy - the taxonony to which the taxon belongs. Default null
   * @param {string} params.output - supported outputs are: "HTML" | "PDF" | "TEXT"
   * @param {number} params.value - the value to be formatted
   * @param {object} params.valueHelper - null or undefined
   *
   * @returns {string} a formatted string
   */
  taxon(params: any) {
    // if specified, formatter get the node name up to the specified
    // level starting from value.
    const ancestorAtLevel = this._getOption(params, "ancestorAtLevel", null);
    // notAvailable is an object with input and output properties
    // if input to be formatted is equal to notAvailable.input
    // then notAvailable.output is returned
    const notAvailable = this._getOption(params, "notAvailable", null);
    // taxonomy
    const taxonomy = this._getOption(params, "taxonomy", null);

    const output = params.output;
    const value = params.value;

    // shallow comparison wanted (like null for undefined / null)
    // eslint-disable-next-line eqeqeq
    if (value == notAvailable["input"]) {
      return notAvailable["output"];
    }

    let formatted = "";

    if (taxonomy == null || !(value in taxonomy)) {
      return notAvailable["output"];
    }

    const getAncestorByLevel = (
      taxonomy: any,
      taxonId: any,
      taxonLevel: any
    ) => {
      var node = taxonomy[taxonId];
      var nodeLevelInt = parseInt(node.type.charAt(0));
      var taxonLevelInt = parseInt(taxonLevel.charAt(0));

      if (nodeLevelInt <= taxonLevelInt || node.parent == null) {
        return taxonId;
      }

      return getAncestorByLevel(taxonomy, node.parent, taxonLevel);
    };

    switch (output) {
      case "HTML": {
        let _formatted = null;
        if (ancestorAtLevel != null) {
          const taxonId = getAncestorByLevel(taxonomy, value, ancestorAtLevel);
          _formatted = taxonomy[taxonId].name;
        } else {
          _formatted = taxonomy[value].name;
        }

        formatted = `<span title="${_formatted}">${_formatted}</span>`;

        break;
      }
      case "PDF":
      case "TEXT":
      default: {
        if (ancestorAtLevel != null) {
          const taxonId = getAncestorByLevel(taxonomy, value, ancestorAtLevel);
          formatted = taxonomy[taxonId].name;
        } else {
          formatted = taxonomy[value].name;
        }

        break;
      }
    }

    return formatted;
  }

  number(params: any) {
    // if positive the string has a style with a green color else
    // if negative has a style with a red color
    const colored = this._getOption(params, "colored", null);
    // a min a max interval of suitable values
    const contraints = this._getOption(params, "constraints", null);
    // how many decimals must be considered
    const decimals = this._getOption(params, "decimals", 2);
    // notAvailable is an object with input and output properties
    // if input to be formatted is equal to notAvailable.input
    // then notAvailable.output is returned
    const notAvailable = this._getOption(params, "notAvailable", null);
    // add percentage symbol after the value
    const isPercentage = this._getOption(params, "isPercentage", false);
    // add plus symbol before the value
    const hasPositiveSign = this._getOption(params, "hasPositiveSign", false);
    const multiplyBy100 = this._getOption(params, "multiplyBy100", true);
    // if true returns 0 when value is 0, an empty string otherwise.
    // Default true
    const zero = this._getOption(params, "zero", true);

    const output = params.output;
    let value = params.value;

    // shallow comparison wanted (like null for undefined / null)
    // eslint-disable-next-line eqeqeq
    if (value == notAvailable["input"]) {
      return notAvailable["output"];
    }

    value = parseFloat(value); // to be sure to work on Number type

    if (isNaN(value)) {
      return notAvailable["output"];
    }

    if (isPercentage && multiplyBy100) {
      value = value * 100;
    }

    if (contraints) {
      if (value > contraints.max) {
        value = contraints.max;
      }
      if (value < contraints.min) {
        value = contraints.min;
      }
    }

    if (zero && value === 0) {
      return (0).toFixed(decimals) + (isPercentage ? "%" : "");
    } else if (!zero && value === 0) {
      return "";
    }

    const valueFixed = Math.abs(value).toFixed(decimals);
    const zeroFixed = (0).toFixed(decimals);

    if (valueFixed === zeroFixed) {
      return zeroFixed + (isPercentage ? "%" : "");
    }

    let formatted =
      (hasPositiveSign && value > 0 ? "+" : "") +
      value.toFixed(decimals) +
      (isPercentage ? "%" : "");

    let color = "";
    switch (output) {
      case "HTML": {
        if (colored) {
          if (
            (colored === "positive" && value > 0) ||
            (colored === "negative" && value < 0)
          ) {
            color = "format-number format-number--positive";
          } else if (
            (colored === "positive" && value < 0) ||
            (colored === "negative" && value > 0)
          ) {
            color = "format-number format-number--negative";
          }
        }
        if (color) {
          formatted = '<span class="' + color + '">' + formatted + "</span>";
        }
        break;
      }
      case "PDF": {
        if (colored) {
          if (
            (colored === "positive" && value > 0) ||
            (colored === "negative" && value < 0)
          ) {
            color = _rate["colors"]["A"];
          } else if (
            (colored === "positive" && value < 0) ||
            (colored === "negative" && value > 0)
          ) {
            color = _rate["colors"]["D"];
          }
        }
        if (color) {
          formatted = '<span color="' + color + '">' + formatted + "</span>";
        }
        break;
      }
      case "TEXT":
      default: {
        break;
      }
    }

    return formatted;
  }

  _getFormatterFallback(property: any) {
    return {
      backendProperty: property,
      options: {
        notAvailable: {
          input: null,
          output: "",
        },
      },
      type: "string",
    };
  }

  _getFormatter(property: any, formatterName: any, type: any) {
    const sourceType = type;
    if (this.properties == null || this.taxonomies == null) {
      console.warn("[formatter] Properties or taxonomies not set", {
        formatterName: formatterName,
        property: property,
        type: sourceType,
      });

      return this._getFormatterFallback(property);
    }

    type =
      type == null || !(type in this.properties)
        ? this.SERVER_TYPE_DEFAULT
        : type;

    if (!(property in this.properties[type])) {
      type = this.SERVER_TYPE_DEFAULT;
    }
    // if property is not even defined in SERVER_TYPE_DEFAULT
    if (!(property in this.properties[type])) {
      console.warn(
        ["[formatter]", property, "for type", sourceType, "not found"].join(
          " "
        ),
        {
          formatterName: formatterName,
          property: property,
          type: sourceType,
        }
      );

      return this._getFormatterFallback(property);
    }

    const formatter = this.properties[type][property]["formatter"];

    if (formatter[formatterName] == null) {
      throw new Error(`Formatter ${formatterName} for ${property} is missing`);
    }

    // taxon support
    if (formatter[formatterName]["type"] === "taxon") {
      formatter[formatterName]["options"]["taxonomy"] =
        this.taxonomies[formatter[formatterName]["taxonomyName"]];
    }

    return formatter[formatterName];
  }

  _resolveReferenceProperties(formatter: any, source: any, target: any) {
    if (target != null) {
      if (formatter["referenceProperties"] != null) {
        for (const key in formatter["referenceProperties"]) {
          target[key] = source[formatter["referenceProperties"][key]];
        }
      }
    }
  }
  _commonHtmlPdfText(
    property: any,
    formatterName: any,
    value: any,
    valueHelper: any,
    type: any,
    output: any
  ) {
    const formatter = this._getFormatter(
      property,
      formatterName,
      type.toLowerCase()
    );

    if (valueHelper != null) {
      const propertyServer = formatter["backendProperty"];

      if (propertyServer in valueHelper) {
        value = valueHelper[propertyServer];
      }

      this._resolveReferenceProperties(formatter, valueHelper, valueHelper);
    }

    return this[formatter["type"]]({
      options: formatter["options"],
      output: output,
      value: value,
      valueHelper: valueHelper,
    });
  }
  text(
    property: any,
    formatterName: any,
    value: any,
    valueHelper: any,
    type: any
  ) {
    return this._commonHtmlPdfText(
      property,
      formatterName,
      value,
      valueHelper,
      type,
      "TEXT"
    );
  }

  rating(params: any) {
    const hasTrendArrow = this._getOption(params, "hasTrendArrow", true);
    // notAvailable is an object with input and output properties
    // if input to be formatted is equal to notAvailable.input
    // then notAvailable.output is returned
    const notAvailable = this._getOption(params, "notAvailable", {
      input: null,
      output: "",
    });

    const output = params.output;
    const value = params.value;
    const valueHelper = params.valueHelper;

    // shallow comparison wanted (like null for undefined / null)
    // eslint-disable-next-line eqeqeq
    if (value == notAvailable["input"]) {
      return notAvailable["output"];
    }

    let className: any = null;
    let formatted = "";
    const scale = _rate["rating"];
    let rateMeta = scale[value as keyof (typeof _rate)["rating"]];

    if (rateMeta == null) {
      rateMeta = {
        class: "format-rate format-rate--missing",
        color: "#FF0000",
        colorChart: "#FF0000",
        label: "-",
        value: Infinity,
      };
    }

    switch (output) {
      case "HTML": {
        if (
          valueHelper == null ||
          !(
            "rateChange" in valueHelper &&
            "rateDate" in valueHelper &&
            "ratePrev" in valueHelper
          )
        ) {
          throw new Error(
            'Missing valueHelper or one of "rateChange", "rateDate", "ratePrev" properties in valueHelper'
          );
        }

        const rateChange = valueHelper["rateChange"];
        const rateDate = valueHelper["rateDate"];
        const ratePrev = valueHelper["ratePrev"];
        const ratePrevMeta = scale[ratePrev as keyof (typeof _rate)["rating"]];

        if (rateChange === 0) {
          if (ratePrev !== undefined) {
            // from to
            if (ratePrev !== 0 && value !== 0) {
              formatted = `<strong class="${rateMeta["class"]}" title="Today move from ${ratePrevMeta["label"]} to ${rateMeta["label"]}">${rateMeta["label"]}</strong>`;

              if (hasTrendArrow === true) {
                className =
                  value > ratePrev
                    ? "format-alert i-upgrade"
                    : "format-alert i-downgrade";

                formatted = `${formatted}<span class="${className}"></span>`;
              }
            }
            // first rate
            if (ratePrev === 0 && value !== 0) {
              formatted = `<strong class="${rateMeta["class"]}" title="Just rated!">${rateMeta["label"]}</strong>`;

              if (hasTrendArrow === true) {
                className = "format-alert i-alert";

                formatted = `${formatted}<strong class="${className}">!</strong>`;
              }
            }
          }
        } else {
          let rateMetaTitle: any = null;
          // If there is no rateDate, do not generate the
          // title attribute on element
          if (rateDate != null) {
            rateMetaTitle =
              "Rated on " +
              this.date({
                options: {
                  notAvailable: {
                    input: null,
                    output: "",
                  },
                },
                output: "TEXT",
                value: rateDate,
              });
          }

          formatted = [
            '<strong class="',
            rateMeta["class"],
            rateMetaTitle != null ? '" title="' + rateMetaTitle : "",
            '">',
            rateMeta["label"],
            "</strong>",
          ].join("");
        }

        break;
      }
      case "PDF": {
        formatted = [
          '<strong><span color="',
          rateMeta["color"],
          '">',
          rateMeta["label"],
          "</span></strong>",
        ].join("");

        break;
      }
      case "TEXT":
      default: {
        formatted = rateMeta.label;

        break;
      }
    }

    return formatted;
  }

  /**
   * Format date
   *
   * @param {object}  params - value and formatting options
   * @param {object}  params.options - formatting options
   * @param {array}   params.options.format - The format to be used. Default ["Y", "m", "D"]
   * @param {boolean} params.options.isMillisecond - if value unit is millisecond instead of day. Default false
   * @param {object}  params.options.notAvailable - (Required) Directive to manage not available case
   * @param {any}     params.options.notAvailable.input - not available value
   * @param {any}     params.options.notAvailable.output - what to return if params.value === to params.options.notAvailable.input
   * @param {string}  params.options.separator - The separator to be used. Default '-'
   * @param {string}  params.output - supported outputs are: "HTML" | "PDF" | "TEXT"
   * @param {number}  params.value - the value to be formatted
   * @param {object}  params.valueHelper - null or undefined
   *
   * @returns {string} a formatted string
   */
  date(params: {
    options: {
      format?: string[];
      isMillisecond?: boolean;
      notAvailable: {
        input: any;
        output: any;
      };
      separator?: string;
    };
    output: OutputType;
    value: number;
    valueHelper?: any;
  }) {
    // output format
    const format = this._getOption(params, "format", ["Y", "m", "D"]);
    // value unit is millisecond: default is day
    const isMillisecond = this._getOption(params, "isMillisecond", false);
    // notAvailable is an object with input and output properties
    // if input to be formatted is equal to notAvailable.input
    // then notAvailable.output is returned
    const notAvailable = this._getOption(params, "notAvailable", null);
    // the date separator
    const separator = this._getOption(params, "separator", "-");

    const output = params.output;
    let value = params.value;

    // shallow comparison wanted (like null for undefined / null)
    // eslint-disable-next-line eqeqeq
    if (value == notAvailable["input"]) {
      return notAvailable["output"];
    }

    if (!isMillisecond) {
      value = TDate.daysToMilliseconds(value);
    }

    let formatted = "";
    const rawDate = new Date(value);
    const Y = rawDate.getUTCFullYear();
    const d = rawDate.getUTCDate();
    const m = rawDate.getUTCMonth() + 1;
    const day = rawDate.getUTCDay() as keyof typeof DAYS;

    const M = (m < 10 ? "0" + m : "" + m) as keyof typeof MONTHS;
    const D = d < 10 ? "0" + d : "" + d;

    switch (output) {
      case "HTML":
      case "PDF":
      case "TEXT":
      default: {
        let token: any = null;
        for (let i = 0, length = format.length; i < length; i++) {
          switch (format[i]) {
            case "DAY":
            case "day": {
              token = DAYS[day];
              break;
            }
            case "D":
            case "d": {
              token = D;
              break;
            }
            case "m": {
              token = M;
              break;
            }
            case "M": {
              token = MONTHS[M];
              break;
            }
            case "y":
            case "Y": {
              token = Y;
              break;
            }
            default:
              break;
          }
          formatted += token;
          if (i < length - 1) {
            formatted += separator;
          }
        }
        break;
      }
    }

    return formatted;
  }

  _getOption(params: any, option: any, defaultValue: any) {
    defaultValue = defaultValue !== undefined ? defaultValue : null;

    let _option =
      "options" in params &&
      params["options"] !== undefined &&
      params["options"] != null &&
      option in params["options"] &&
      params["options"][option] !== undefined &&
      params["options"][option] != null
        ? params["options"][option]
        : null;

    if (_option == null) {
      _option = defaultValue;
    }

    return _option;
  }
}

const _rate = {
  // --------------------------------------------------------------------------
  // IMPORTANT
  // --------------------------------------------------------------------------
  // because of PDF report information are duplicated also in CSS.
  // If you change colors here remember to update colors in CSS file.

  colors: {
    A: "#008000",
    B: "#8BBC00",
    C: "#F48400",
    D: "#F00000",
    U: "#D3D3D3", //'#EFEFEF'
  },

  rating: {
    "2": {
      class: "format-rate format-rate--A",
      color: "#008000",
      colorChart: "#008000",
      label: "A",
      value: 2,
    },
    "1": {
      class: "format-rate format-rate--B",
      color: "#8BBC00",
      colorChart: "#8BBC00",
      label: "B",
      value: 1,
    },
    "0": {
      class: "format-rate format-rate--U",
      color: "#000000",
      colorChart: "#D3D3D3",
      label: "-",
      value: 0,
    },
    "-1": {
      class: "format-rate format-rate--C",
      color: "#F48400",
      colorChart: "#F48400",
      label: "C",
      value: -1,
    },
    "-2": {
      class: "format-rate format-rate--D",
      color: "#F00000",
      colorChart: "#F00000",
      label: "D",
      value: -2,
    },
    A: {
      class: "format-rate format-rate--A",
      color: "#008000",
      colorChart: "#008000",
      label: "A",
      value: 2,
    },
    B: {
      class: "format-rate format-rate--B",
      color: "#8BBC00",
      colorChart: "#8BBC00",
      label: "B",
      value: 1,
    },
    C: {
      class: "format-rate format-rate--C",
      color: "#F48400",
      colorChart: "#F48400",
      label: "C",
      value: -1,
    },
    D: {
      class: "format-rate format-rate--D",
      color: "#F00000",
      colorChart: "#F00000",
      label: "D",
      value: -2,
    },
    U: {
      class: "format-rate format-rate--U",
      color: "#000000",
      colorChart: "#D3D3D3",
      label: "-",
      value: 0,
    },
  },

  // TCR
  trendCaptureRating: {
    "4": {
      class: "format-rate format-rate--A",
      color: "#008000",
      colorChart: "#008000",
      label: "A",
      rateClass: "A", // deprecated: use tcrClass
      tcrClass: "A",
      value: 4,
    },
    "3": {
      class: "format-rate format-rate--A",
      color: "#008000",
      colorChart: "#008000",
      label: "A-",
      rateClass: "A", // deprecated: use tcrClass
      tcrClass: "A",
      value: 3,
    },
    "2": {
      class: "format-rate format-rate--B",
      color: "#8BBC00",
      colorChart: "#8BBC00",
      label: "B+",
      rateClass: "B", // deprecated: use tcrClass
      tcrClass: "B",
      value: 2,
    },
    "1": {
      class: "format-rate format-rate--B",
      color: "#8BBC00",
      colorChart: "#8BBC00",
      label: "B",
      rateClass: "B", // deprecated: use tcrClass
      tcrClass: "B",
      value: 1,
    },
    "0": {
      class: "format-rate format-rate--B",
      color: "#8BBC00",
      colorChart: "#8BBC00",
      label: "B-",
      rateClass: "B", // deprecated: use tcrClass
      tcrClass: "B",
      value: 0,
    },
    "-1": {
      class: "format-rate format-rate--C",
      color: "#F48400",
      colorChart: "#F48400",
      label: "C+",
      rateClass: "C", // deprecated: use tcrClass
      tcrClass: "C",
      value: -1,
    },
    "-2": {
      class: "format-rate format-rate--C",
      color: "#F48400",
      colorChart: "#F48400",
      label: "C",
      rateClass: "C", // deprecated: use tcrClass
      tcrClass: "C",
      value: -2,
    },
    "-3": {
      class: "format-rate format-rate--C",
      color: "#F48400",
      colorChart: "#F48400",
      label: "C-",
      rateClass: "C", // deprecated: use tcrClass
      tcrClass: "C",
      value: -3,
    },
    "-4": {
      class: "format-rate format-rate--D",
      color: "#F00000",
      colorChart: "#F00000",
      label: "D+",
      rateClass: "D", // deprecated: use tcrClass
      tcrClass: "D",
      value: -4,
    },
    "-5": {
      class: "format-rate format-rate--D",
      color: "#F00000",
      colorChart: "#F00000",
      label: "D",
      rateClass: "D", // deprecated: use tcrClass
      tcrClass: "D",
      value: -5,
    },
    "-9999": {
      class: "format-rate format-rate--U",
      color: "#000000",
      colorChart: "#D3D3D3",
      label: "-",
      rateClass: "U", // deprecated: use tcrClass
      tcrClass: "U",
      value: null,
    },
    CASH: {
      class: "format-rate format-rate--cash",
      color: "#000000",
      colorChart: "#FFFFFF",
      label: "-",
      rateClass: "cash",
      value: null,
    },
    U: {
      class: "format-rate format-rate--U",
      color: "#000000",
      colorChart: "#D3D3D3",
      label: "-",
      rateClass: "U",
      value: null,
    },
  },
};
